<template>
	<div class="min-h-screen bg-gray-50 font-sans">
		<!-- Header -->
		<header class="bg-white border-b border-gray-200 py-4 px-3">
			<div class="w-full flex justify-between items-center flex-wrap gap-2">
				<div class="flex items-center gap-4">
					<button
						type="button"
						class="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 border-none text-gray-700 cursor-pointer transition-colors hover:bg-gray-200"
						title="Back to Offering Options"
						@click="goBack"
					>
						<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
							<path stroke-linecap="round" stroke-linejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
						</svg>
					</button>
					<div>
						<h1 class="text-2xl font-semibold text-gray-900 m-0">Edit Offering Option</h1>
						<p class="text-sm text-gray-500 mt-1 mb-0">ID: {{ routeId }}</p>
					</div>
				</div>
				<!-- Desktop Save Button -->
				<button
					type="button"
					class="save-btn-desktop inline-flex items-center gap-2 bg-orange-7 text-white py-2.5 px-4 rounded-lg text-sm font-medium border-none cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-70 disabled:cursor-not-allowed"
					:disabled="isSaving"
					@click="saveChanges"
				>
					<svg v-if="isSaving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
						<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
						<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
					</svg>
					<svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
					</svg>
					{{ isSaving ? 'Saving...' : 'Save Changes' }}
				</button>
			</div>
		</header>

		<!-- Main Content -->
		<main class="w-full p-3">
			<!-- Loading State -->
			<div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
				<svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
					<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
					<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
				</svg>
				<span class="ml-3 text-gray-500 text-sm">Loading offering option...</span>
			</div>

			<!-- Content Grid -->
			<div v-else class="grid grid-cols-1 gap-4 edit-content-grid">
				<!-- Properties Card -->
				<div class="bg-white rounded-xl shadow-sm overflow-hidden">
					<!-- Card Header -->
					<div class="py-4 px-6 border-b border-gray-200 flex items-center gap-3">
						<svg class="w-5 h-5 text-orange-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
							<path stroke-linecap="round" stroke-linejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
							<path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
						</svg>
						<h2 class="text-lg font-semibold text-gray-900 m-0">Properties</h2>
					</div>

					<!-- Form Fields -->
					<div class="p-6">
						<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
							<!-- Name -->
							<div>
								<label class="block text-sm font-medium text-gray-700 mb-2">
									Name
								</label>
								<input
									v-model="offeringOption.name"
									type="text"
									class="w-full py-2.5 px-3.5 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2 box-border"
								>
							</div>

							<!-- Description -->
							<div>
								<label class="block text-sm font-medium text-gray-700 mb-2">
									Description
								</label>
								<input
									v-model="offeringOption.description"
									type="text"
									class="w-full py-2.5 px-3.5 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2 box-border"
								>
							</div>

							<!-- Serial Number -->
							<div>
								<label class="block text-sm font-medium text-gray-700 mb-2">
									Serial Number
								</label>
								<input
									v-model.number="offeringOption.serialnumber"
									type="number"
									class="w-full py-2.5 px-3.5 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2 box-border"
								>
							</div>
						</div>

						<!-- Toggle Switches -->
						<div class="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
							<!-- Show During Edit -->
							<div class="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
								<label class="text-sm font-medium text-gray-700">Show During Edit</label>
								<button
									type="button"
									class="relative w-11 h-6 rounded-full border-none cursor-pointer transition-colors"
									:class="offeringOption.showduringedit ? 'bg-orange-7' : 'bg-gray-300'"
									@click="offeringOption.showduringedit = offeringOption.showduringedit ? 0 : 1"
								>
									<span
										class="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform"
										:class="offeringOption.showduringedit ? 'translate-x-5.5' : 'translate-x-0.5'"
									></span>
								</button>
							</div>

							<!-- Show After Edit -->
							<div class="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
								<label class="text-sm font-medium text-gray-700">Show After Edit</label>
								<button
									type="button"
									class="relative w-11 h-6 rounded-full border-none cursor-pointer transition-colors"
									:class="offeringOption.showafteredit ? 'bg-orange-7' : 'bg-gray-300'"
									@click="offeringOption.showafteredit = offeringOption.showafteredit ? 0 : 1"
								>
									<span
										class="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform"
										:class="offeringOption.showafteredit ? 'translate-x-5.5' : 'translate-x-0.5'"
									></span>
								</button>
							</div>

							<!-- Show PDP -->
							<div class="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
								<label class="text-sm font-medium text-gray-700">Show PDP</label>
								<button
									type="button"
									class="relative w-11 h-6 rounded-full border-none cursor-pointer transition-colors"
									:class="offeringOption.showpdp ? 'bg-orange-7' : 'bg-gray-300'"
									@click="offeringOption.showpdp = offeringOption.showpdp ? 0 : 1"
								>
									<span
										class="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform"
										:class="offeringOption.showpdp ? 'translate-x-5.5' : 'translate-x-0.5'"
									></span>
								</button>
							</div>

							<!-- Production -->
							<div class="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
								<label class="text-sm font-medium text-gray-700">Production</label>
								<button
									type="button"
									class="relative w-11 h-6 rounded-full border-none cursor-pointer transition-colors"
									:class="offeringOption.production ? 'bg-orange-7' : 'bg-gray-300'"
									@click="offeringOption.production = offeringOption.production ? 0 : 1"
								>
									<span
										class="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform"
										:class="offeringOption.production ? 'translate-x-5.5' : 'translate-x-0.5'"
									></span>
								</button>
							</div>
						</div>
					</div>
				</div>

				<!-- Option Values Card -->
				<div class="bg-white rounded-xl shadow-sm overflow-hidden">
					<!-- Card Header -->
					<div class="py-4 px-6 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4">
						<div class="flex items-center gap-3">
							<svg class="w-5 h-5 text-orange-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
								<path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
							</svg>
							<h2 class="text-lg font-semibold text-gray-900 m-0">Option Values</h2>
							<span class="inline-flex items-center py-1 px-2.5 text-xs font-medium bg-orange-2 text-orange-7 rounded-full">
								{{ optionValues.length }} values
							</span>
						</div>
						<div class="flex items-center gap-3">
							<select
								v-model="selectedLanguage"
								class="py-1.5 pl-2 pr-6 border border-gray-300 rounded-md text-sm bg-white cursor-pointer outline-none"
								@change="loadValueTranslations"
							>
								<option v-for="lang in languages" :key="lang.id" :value="lang.id">
									{{ lang.name }}
								</option>
							</select>
							<button
								type="button"
								class="inline-flex items-center gap-1 py-2 px-3 bg-transparent border border-orange-4 text-orange-7 rounded-lg text-sm font-medium cursor-pointer transition-colors hover:bg-orange-1"
								@click="showAddValueModal = true"
							>
								<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
									<path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
								</svg>
								Add Value
							</button>
						</div>
					</div>

					<!-- Values Table -->
					<div class="overflow-x-auto">
						<div v-if="optionValues.length === 0" class="text-center py-12 px-4">
							<svg class="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
								<path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
							</svg>
							<p class="text-sm text-gray-500 m-0">No option values yet. Add your first value.</p>
						</div>
						<table v-else class="w-full border-collapse">
							<thead>
								<tr class="bg-gray-50">
									<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ID</th>
									<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Value</th>
									<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Header</th>
									<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Translation</th>
									<th class="py-3 px-6 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
								</tr>
							</thead>
							<tbody class="divide-y divide-gray-200">
								<tr v-for="val in optionValues" :key="val.id" class="hover:bg-gray-50">
									<td class="py-3 px-6 text-sm text-gray-500">{{ val.id }}</td>
									<td class="py-3 px-6 text-sm text-gray-900 font-medium">{{ val.value }}</td>
									<td class="py-3 px-6 text-sm text-gray-500">{{ val.header }}</td>
									<td class="py-3 px-6">
										<input
											v-model="val.translation"
											type="text"
											class="w-full py-1.5 px-2 border border-gray-300 rounded text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
											@blur="saveValueTranslation(val)"
										>
									</td>
									<td class="py-3 px-6 text-right">
										<button
											type="button"
											class="p-1.5 text-red-500 rounded transition-colors hover:bg-red-50"
											@click="confirmDeleteValue(val)"
										>
											<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
												<path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
											</svg>
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>

				<!-- Mobile Save Button -->
				<div class="save-btn-mobile">
					<button
						type="button"
						class="w-full inline-flex items-center justify-center gap-2 bg-orange-7 text-white py-3 px-4 rounded-lg text-sm font-medium border-none cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-70 disabled:cursor-not-allowed"
						:disabled="isSaving"
						@click="saveChanges"
					>
						<svg v-if="isSaving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
							<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
							<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
						</svg>
						<svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
							<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
						</svg>
						{{ isSaving ? 'Saving...' : 'Save Changes' }}
					</button>
				</div>
			</div>
		</main>

		<!-- Add Value Modal -->
		<div
			v-if="showAddValueModal"
			class="fixed inset-0 z-50 flex items-center justify-center p-4"
		>
			<div class="absolute inset-0 bg-black/50" @click="showAddValueModal = false"></div>
			<div class="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
				<h3 class="text-lg font-semibold text-gray-900 mb-4">Add Option Value</h3>
				<div class="mb-4">
					<label class="block text-sm font-medium text-gray-700 mb-2">Value</label>
					<input
						v-model="newValueInput"
						type="text"
						placeholder="Enter value"
						class="w-full py-2.5 px-3.5 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
					>
				</div>
				<div class="flex justify-end gap-3">
					<button
						type="button"
						class="py-2 px-4 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg cursor-pointer transition-colors hover:bg-gray-200"
						@click="showAddValueModal = false"
					>
						Cancel
					</button>
					<button
						type="button"
						class="py-2 px-4 text-sm font-medium text-white bg-orange-7 rounded-lg cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-70"
						:disabled="isAddingValue"
						@click="addOptionValue"
					>
						{{ isAddingValue ? 'Adding...' : 'Add Value' }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import Swal from 'sweetalert2';
import OfferingOptionService from 'services/OfferingOptionService';
import OfferingOptionValueService from 'services/OfferingOptionValueService';
import LanguageService from 'services/LanguageService';
import TranslationService from 'services/TranslationService';

export default defineComponent({
	name: 'EditOfferingOptionPage',
	setup() {
		const router = useRouter();
		const route = useRoute();

		const routeId = computed(() => route.params.id as string);
		const offeringOption = ref<any>({});
		const optionValues = ref<any[]>([]);
		const languages = ref<any[]>([]);
		const selectedLanguage = ref<number>(1);
		const isLoading = ref(true);
		const isSaving = ref(false);
		const showAddValueModal = ref(false);
		const newValueInput = ref('');
		const isAddingValue = ref(false);

		const goBack = () => {
			router.push('/offering-options');
		};

		const loadData = async () => {
			isLoading.value = true;
			try {
				const [optionData, valuesData, languagesData] = await Promise.all([
					OfferingOptionService.get(routeId.value),
					OfferingOptionValueService.getByOption(routeId.value),
					LanguageService.getAll(),
				]);
				offeringOption.value = optionData;
				optionValues.value = valuesData;
				languages.value = languagesData;
				await loadValueTranslations();
			} catch (error: any) {
				console.error('Failed to load data:', error);
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to load offering option',
					icon: 'error',
					customClass: {
						popup: 'windmill-swal-popup',
						title: 'windmill-swal-title',
						htmlContainer: 'windmill-swal-content',
						confirmButton: 'windmill-swal-confirm',
					},
				});
			} finally {
				isLoading.value = false;
			}
		};

		const loadValueTranslations = async () => {
			try {
				for (const val of optionValues.value) {
					const translation = await TranslationService.getOptionValueTranslation(
						val.id,
						selectedLanguage.value
					);
					val.translation = translation?.translation || '';
				}
			} catch (error) {
				console.error('Failed to load translations:', error);
			}
		};

		const saveValueTranslation = async (val: any) => {
			try {
				await TranslationService.saveOptionValueTranslation(
					val.id,
					selectedLanguage.value,
					val.translation
				);
			} catch (error) {
				console.error('Failed to save translation:', error);
			}
		};

		const saveChanges = async () => {
			isSaving.value = true;
			try {
				await OfferingOptionService.update(routeId.value, offeringOption.value);
				Swal.fire({
					toast: true,
					position: 'top-end',
					icon: 'success',
					title: 'Offering option saved',
					showConfirmButton: false,
					timer: 2000,
					timerProgressBar: true,
				});
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to save offering option',
					icon: 'error',
					customClass: {
						popup: 'windmill-swal-popup',
						title: 'windmill-swal-title',
						htmlContainer: 'windmill-swal-content',
						confirmButton: 'windmill-swal-confirm',
					},
				});
			} finally {
				isSaving.value = false;
			}
		};

		const addOptionValue = async () => {
			if (!newValueInput.value.trim()) return;
			isAddingValue.value = true;
			try {
				const newValue = await OfferingOptionValueService.create({
					offeringoptionId: parseInt(routeId.value),
					value: newValueInput.value,
				});
				optionValues.value.push({ ...newValue, translation: '' });
				newValueInput.value = '';
				showAddValueModal.value = false;
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to add option value',
					icon: 'error',
				});
			} finally {
				isAddingValue.value = false;
			}
		};

		const confirmDeleteValue = async (val: any) => {
			const result = await Swal.fire({
				title: 'Delete Value?',
				text: `Are you sure you want to delete "${val.value}"?`,
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#dc2626',
				confirmButtonText: 'Delete',
				customClass: {
					popup: 'windmill-swal-popup',
					title: 'windmill-swal-title',
					htmlContainer: 'windmill-swal-content',
					confirmButton: 'windmill-swal-confirm',
					cancelButton: 'windmill-swal-cancel',
				},
			});

			if (result.isConfirmed) {
				try {
					await OfferingOptionValueService.delete(val.id);
					optionValues.value = optionValues.value.filter((v) => v.id !== val.id);
				} catch (error: any) {
					Swal.fire({
						title: 'Error',
						text: error.message || 'Failed to delete option value',
						icon: 'error',
					});
				}
			}
		};

		onMounted(() => {
			loadData();
		});

		return {
			routeId,
			offeringOption,
			optionValues,
			languages,
			selectedLanguage,
			isLoading,
			isSaving,
			showAddValueModal,
			newValueInput,
			isAddingValue,
			goBack,
			loadValueTranslations,
			saveValueTranslation,
			saveChanges,
			addOptionValue,
			confirmDeleteValue,
		};
	},
});
</script>

<style scoped>
@media (min-width: 1024px) {
	.edit-content-grid {
		grid-template-columns: 1fr 1fr !important;
	}
}

@media (max-width: 640px) {
	.save-btn-desktop {
		display: none !important;
	}
	.save-btn-mobile {
		display: block !important;
	}
}
</style>
